import EmailIcon from "@mui/icons-material/Email";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Fragment, memo, useMemo } from "react";

import type { ScoutConversationPrivateDocumentRow } from "../../hooks/useScoutConversationPrivateDocuments";
import type { ScoutChatMessage, ScoutFilePart, ScoutTextPart } from "./scoutChatMessage";

import {
  splitScoutQuestionCitation,
  stripScoutInternalEventLineFromText,
} from "../../hooks/scoutInternalEvents";
import { isTextExtractionDocument } from "../../hooks/scoutPrivateDocumentQuery";
import {
  isScoutPrivateDocumentExtractionWatchPartUrl,
  parseScoutExtractionWatchDocumentIdsFromText,
  stripScoutExtractionWatchMarkerFromText,
} from "../scoutExtractionWatchMessage";
import { ScoutLargeDocumentExtractionProgressCard } from "../ScoutLargeDocumentExtractionProgressCard";
import { SCOUT_LARGE_DOCUMENT_EXTRACTION_PROGRESS_STATUSES } from "../scoutLargeDocumentJobUi";
import { SCOUT_SKILL_SLUG_CLASS } from "../skills/remarkScoutSkillSlugs";
import { ScoutUserMarkdown } from "./MessageContent";
import { ScoutFileDisplay } from "./ScoutFileDisplay";
import { useScoutMessageSpacing } from "./scoutMessageSpacing";

/** Empty gap between the conversation title stack and a newly sent user bubble. */
export const SCOUT_TURN_ANCHOR_TOP_GAP_PX = 16;

function resolveExtractionWatchDocumentIds(message: ScoutChatMessage): string[] {
  const documentIds = new Set<string>();

  for (const part of message.parts) {
    if (part.type === "file" && isScoutPrivateDocumentExtractionWatchPartUrl(part.url)) {
      const documentId = part.document?.id;
      if (documentId) {
        documentIds.add(documentId);
      }
      const urlId = part.url?.replace("encamp-private-document-extraction://", "");
      if (urlId) {
        documentIds.add(urlId);
      }
    }
  }

  for (const part of message.parts) {
    if (part.type === "text") {
      const partDocumentIds = parseScoutExtractionWatchDocumentIdsFromText(part.text);
      for (const documentId of partDocumentIds) {
        documentIds.add(documentId);
      }
    }
  }

  return [...documentIds];
}

type UserMessageProps = {
  message: ScoutChatMessage;
  tenantId?: string;
  conversationId?: string;
  privateDocuments?: ScoutConversationPrivateDocumentRow[];
  /** When true, this bubble is the scroll target for a newly sent turn. */
  isTurnAnchor?: boolean;
  /** Show the author email on user messages. Defaults to false. */
  showAuthorEmail?: boolean;
};

function MessageAuthorCaption({
  show,
  email,
}: {
  show: boolean;
  email: string | null | undefined;
}) {
  if (!show || !email) {
    return null;
  }
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid="scout-message-author"
      sx={{ userSelect: "none" }}
    >
      {email}
    </Typography>
  );
}

export const UserMessage = memo(function UserMessage({
  message,
  tenantId,
  conversationId,
  privateDocuments = [],
  isTurnAnchor = false,
  showAuthorEmail = false,
}: UserMessageProps) {
  const extractionWatchDocumentIds = useMemo(
    () => resolveExtractionWatchDocumentIds(message),
    [message]
  );
  const { rowPaddingX } = useScoutMessageSpacing();

  const isAbort = message.parts.some((part) => part.type === "abort");
  if (isAbort) {
    return (
      <Box
        {...(isTurnAnchor ? { "data-scout-turn-anchor": "true" } : {})}
        sx={{
          display: "flex",
          justifyContent: "center",
          // Use padding (not scrollMarginTop) so the title gap is empty canvas,
          // not a peek of the prior message's copy/feedback toolbar.
          ...(isTurnAnchor
            ? { pt: `${SCOUT_TURN_ANCHOR_TOP_GAP_PX}px`, pb: 1, scrollMarginTop: 0 }
            : { py: 1 }),
          px: rowPaddingX,
        }}
      >
        <Stack spacing={0.25} alignItems="center">
          <MessageAuthorCaption show={showAuthorEmail} email={message.author?.email} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textAlign: "center",
              letterSpacing: 0.2,
              userSelect: "none",
            }}
          >
            User cancelled the response.
          </Typography>
        </Stack>
      </Box>
    );
  }

  const textParts = message.parts.filter((part): part is ScoutTextPart => part.type === "text");
  const fileParts = message.parts.filter(
    (part): part is ScoutFilePart =>
      part.type === "file" && !isScoutPrivateDocumentExtractionWatchPartUrl(part.url)
  );

  const displayTextParts = textParts
    .map((part) => stripScoutExtractionWatchMarkerFromText(part.text))
    .map((text) => stripScoutInternalEventLineFromText(text))
    .map((text) => splitScoutQuestionCitation(text))
    .filter(({ text, citation }) => text.length > 0 || citation !== undefined);

  // The progress card returns null for docs that aren't in active large-file
  // extraction. When the only thing this message would render is a set of
  // no-op progress cards (e.g. the synthetic confirm message for a tenant
  // document proposal where every doc is NATIVE-mode), suppress the whole
  // bubble so the chat doesn't hold open ~28px of empty padding before
  // Scout's follow-up turn.
  const hasVisibleExtractionWatch = extractionWatchDocumentIds.some((privateDocumentId) => {
    const document = privateDocuments.find((row) => row.id === privateDocumentId);
    return (
      document !== undefined &&
      isTextExtractionDocument(document) &&
      SCOUT_LARGE_DOCUMENT_EXTRACTION_PROGRESS_STATUSES.has(document.extractionStatus)
    );
  });

  if (displayTextParts.length === 0 && fileParts.length === 0 && !hasVisibleExtractionWatch) {
    return null;
  }

  return (
    <Stack
      gap={1}
      {...(isTurnAnchor ? { "data-scout-turn-anchor": "true" } : {})}
      sx={{
        display: "flex",
        flexDirection: "column",
        // Use padding (not scrollMarginTop) so the title gap is empty canvas,
        // not a peek of the prior message's copy/feedback toolbar.
        ...(isTurnAnchor
          ? { pt: `${SCOUT_TURN_ANCHOR_TOP_GAP_PX}px`, pb: 1.75, scrollMarginTop: 0 }
          : { py: 1.75 }),
        px: rowPaddingX,
      }}
    >
      {(displayTextParts.length > 0 || fileParts.length > 0) && (
        <Stack
          gap={1}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <MessageAuthorCaption show={showAuthorEmail} email={message.author?.email} />
          {message.frontMessageId ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <EmailIcon sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary" sx={{ userSelect: "none" }}>
                Customer email
              </Typography>
            </Box>
          ) : null}
          {fileParts.length > 0 ? (
            <Stack spacing={1} useFlexGap flexWrap="wrap">
              {fileParts.map((part) => (
                <ScoutFileDisplay key={part.id} part={part} />
              ))}
            </Stack>
          ) : null}
          {displayTextParts.length > 0 ? (
            <Box
              {...(message.frontMessageId ? { "data-testid": "front-email-message" } : {})}
              sx={(theme) => ({
                maxWidth: "min(600px, 100%)",
                p: 2,
                borderRadius: message.frontMessageId ? 2 : "16px",
                bgcolor: theme.palette.scoutUserBubble,
                color: theme.palette.text.primary,
                overflowWrap: "break-word",
                [`& .${SCOUT_SKILL_SLUG_CLASS}`]: {
                  color: alpha(theme.palette.primary.main, 0.85),
                },
                "& > :first-child, & > :first-child > :first-child": { mt: 0 },
                "& > :last-child, & > :last-child > :last-child": { mb: 0 },
                ...(message.frontMessageId
                  ? { border: 1, borderColor: theme.palette.divider }
                  : {}),
              })}
            >
              {displayTextParts.map(({ text, citation }, index) => (
                <Fragment key={`${message.id}-text-${index}`}>
                  {text && message.frontMessageId ? (
                    // Synced Front emails are not markdown; keep their text verbatim.
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.5,
                        "&:not(:last-of-type)": { mb: 1 },
                      }}
                    >
                      {text}
                    </Typography>
                  ) : null}
                  {text && !message.frontMessageId ? (
                    <ScoutUserMarkdown content={text} blockKey={`${message.id}-text-${index}`} />
                  ) : null}
                  {citation ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {citation}
                    </Typography>
                  ) : null}
                </Fragment>
              ))}
            </Box>
          ) : null}
        </Stack>
      )}

      {extractionWatchDocumentIds.map((privateDocumentId) => (
        <ScoutLargeDocumentExtractionProgressCard
          key={`${message.id}-extraction-${privateDocumentId}`}
          tenantId={tenantId}
          conversationId={conversationId}
          privateDocumentId={privateDocumentId}
          documents={privateDocuments}
        />
      ))}
    </Stack>
  );
});
UserMessage.displayName = "UserMessage";
